// Comparisons topic: session building for the comparative/superlative drill.
// Pure (no React) so generation + grading can be unit-tested on its own.
//
// Unlike the contraction and object-pronoun drills (fixed, hand-authored
// sentence banks), this drill procedurally generates items the same way the
// conjugation drill does: an authored attribute/subject/verb dataset is
// recombined at session-build time, so the learner sees fresh data-driven
// prompts instead of memorizing a fixed list. The learner is shown the raw
// data in English and must produce the Portuguese sentence, picking the
// right frame (mais/menos/tão/tanto/irregular) themselves — the direction is
// never told to them, only the underlying facts.
//
// Three learner-facing groups, chosen for how intuitive they are rather than
// a 1:1 mapping onto every grammatical subtype:
//   - "Comparison with adjective" — two things: more/less/as (incl. irregulars)
//   - "Superlative"                — most/least in a group of 3+
//   - "Comparison with verbs"      — more/less/as/most/least via a verb
//     (gostar, preferir, querer, amar, adorar, precisar, ter) instead of an
//     adjective — includes the noun/quantity "tanto...quanto" items, since
//     those use "ter" too.
//
// Brazilian Portuguese only. `menor` is always used over the PT-PT "mais
// pequeno" variant, matching the rest of the app's dataset.

import { shuffle } from './phraseBank'

export type ComparativeCategory = 'adjective' | 'superlative' | 'verb'

export type ComparativeCategoryMeta = {
  id: ComparativeCategory
  label: string
  description: string
}

export const COMPARATIVE_CATEGORIES: ComparativeCategoryMeta[] = [
  { id: 'adjective', label: 'Comparison with adjective', description: 'more/less/as between two things' },
  { id: 'superlative', label: 'Superlative', description: 'most/least in a group of 3+' },
  { id: 'verb', label: 'Comparison with verbs', description: 'more/less/as/most/least using a verb' },
]

export const DEFAULT_COMPARATIVE_CATEGORY_IDS: ComparativeCategory[] = COMPARATIVE_CATEGORIES.map((category) => category.id)

// The setup screen's starting checkboxes — narrower than "every category"
// above (which is also the empty-selection fallback in activeGenerators, so
// it has to stay "all"). Adjective + superlative are the core comparative
// frame; verb-based items are worth discovering once that's comfortable.
export const INITIAL_COMPARATIVE_CATEGORY_IDS: ComparativeCategory[] = ['adjective', 'superlative']

// A second, cross-cutting filter: regular adjectives (caro, rápido, …) vs.
// the four suppletive irregulars (bom→melhor, mau/ruim→pior, grande→maior,
// pequeno→menor). The irregulars are the PRD's single highest-value target —
// "mais bom" is the most common learner error — so isolating them from the
// regular forms lets a learner drill just that once the regular frame is
// solid. Only meaningful within "adjective" and "superlative"; every "verb"
// item is tagged 'regular' since none of those verbs have a suppletive form.
export type ComparativeForm = 'regular' | 'irregular'

export type ComparativeFormMeta = {
  id: ComparativeForm
  label: string
  description: string
}

export const COMPARATIVE_FORMS: ComparativeFormMeta[] = [
  { id: 'regular', label: 'Regular', description: 'caro, rápido, difícil…' },
  { id: 'irregular', label: 'Irregular', description: 'bom→melhor, grande→maior…' },
]

export const DEFAULT_COMPARATIVE_FORM_IDS: ComparativeForm[] = COMPARATIVE_FORMS.map((form) => form.id)

// Same idea as INITIAL_COMPARATIVE_CATEGORY_IDS — start with just the
// regular forms; irregulars are worth isolating once the regular frame is
// solid, not thrown in from the very first session.
export const INITIAL_COMPARATIVE_FORM_IDS: ComparativeForm[] = ['regular']

// Fixed session length, matching the other drills' convention — not
// user-configurable since generation is effectively unlimited.
export const COMPARATIVE_SESSION_LENGTH = 10

// One normalized prompt, mapped to a DrillItem by index.tsx.
export type ComparativePrompt = {
  promptLines: string[]
  core: string // the sentence without trailing punctuation
  acceptedCores: string[] // every form graded correct, without trailing punctuation
  translation: string
  // Opt-in word lookups for the prompt's vocabulary — the noun(s)/adjective
  // being compared — shown only if the learner asks. Deliberately excludes
  // whatever the generator is actually testing (mais/menos placement,
  // melhor/pior/maior/menor, tanto agreement…), so a hint unblocks vocabulary
  // without giving away the grammar point.
  vocabHints: Array<{ en: string; pt: string }>
}

type Gender = 'm' | 'f'
type Pool = 'people' | 'objects'

// Whatever is being compared — a named person or a named object. `subject`
// is the sentence-initial form (capitalized); `other` is the form used after
// "do que"/"quanto"/"como" (lowercase for an article+noun object phrase,
// identical to `subject` for a proper name, since names don't change case).
type Comparable = { subject: string; other: string; en: string; gender: Gender }

// Mixed-gender named people, reused across every subtype that compares
// people (age, height, distance — things that naturally describe a person).
const PEOPLE: Comparable[] = [
  { subject: 'Ana', other: 'Ana', en: 'Ana', gender: 'f' },
  { subject: 'Pedro', other: 'Pedro', en: 'Pedro', gender: 'm' },
  { subject: 'Marta', other: 'Marta', en: 'Marta', gender: 'f' },
  { subject: 'Lucas', other: 'Lucas', en: 'Lucas', gender: 'm' },
  { subject: 'Júlia', other: 'Júlia', en: 'Júlia', gender: 'f' },
  { subject: 'Rafael', other: 'Rafael', en: 'Rafael', gender: 'm' },
  { subject: 'Beatriz', other: 'Beatriz', en: 'Beatriz', gender: 'f' },
  { subject: 'Gabriel', other: 'Gabriel', en: 'Gabriel', gender: 'm' },
]

// Named things, for attributes that don't make sense for a person — nobody
// "costs" money or "is 8/10 difficulty," but a car or an exam does. Each
// attribute gets its own realistic set of things people actually compare
// that way — comparing a car's price to a bike's is natural, but comparing
// an exercise's difficulty to a car's isn't, so a single shared object pool
// doesn't work here.
const PRICE_OBJECTS: Comparable[] = [
  { subject: 'O carro', other: 'o carro', en: 'the car', gender: 'm' },
  { subject: 'A bicicleta', other: 'a bicicleta', en: 'the bike', gender: 'f' },
  { subject: 'O celular', other: 'o celular', en: 'the phone', gender: 'm' },
  { subject: 'A casa', other: 'a casa', en: 'the house', gender: 'f' },
  { subject: 'O hotel', other: 'o hotel', en: 'the hotel', gender: 'm' },
  { subject: 'A viagem', other: 'a viagem', en: 'the trip', gender: 'f' },
]

const SPEED_OBJECTS: Comparable[] = [
  { subject: 'O carro', other: 'o carro', en: 'the car', gender: 'm' },
  { subject: 'A bicicleta', other: 'a bicicleta', en: 'the bike', gender: 'f' },
  { subject: 'O trem', other: 'o trem', en: 'the train', gender: 'm' },
  { subject: 'O avião', other: 'o avião', en: 'the plane', gender: 'm' },
  { subject: 'O cavalo', other: 'o cavalo', en: 'the horse', gender: 'm' },
]

const DIFFICULTY_OBJECTS: Comparable[] = [
  { subject: 'A prova', other: 'a prova', en: 'the exam', gender: 'f' },
  { subject: 'O exercício', other: 'o exercício', en: 'the exercise', gender: 'm' },
  { subject: 'A receita', other: 'a receita', en: 'the recipe', gender: 'f' },
  { subject: 'O quebra-cabeça', other: 'o quebra-cabeça', en: 'the puzzle', gender: 'm' },
  { subject: 'A entrevista', other: 'a entrevista', en: 'the interview', gender: 'f' },
]

const QUALITY_OBJECTS: Comparable[] = [
  { subject: 'O restaurante', other: 'o restaurante', en: 'the restaurant', gender: 'm' },
  { subject: 'O hotel', other: 'o hotel', en: 'the hotel', gender: 'm' },
  { subject: 'O filme', other: 'o filme', en: 'the movie', gender: 'm' },
  { subject: 'O celular', other: 'o celular', en: 'the phone', gender: 'm' },
]

const SIZE_OBJECTS: Comparable[] = [
  { subject: 'A casa', other: 'a casa', en: 'the house', gender: 'f' },
  { subject: 'O apartamento', other: 'o apartamento', en: 'the apartment', gender: 'm' },
  { subject: 'O quarto', other: 'o quarto', en: 'the room', gender: 'm' },
  { subject: 'A loja', other: 'a loja', en: 'the store', gender: 'f' },
  { subject: 'O quintal', other: 'o quintal', en: 'the yard', gender: 'm' },
]

const TEMPERATURE_OBJECTS: Comparable[] = [
  { subject: 'O café', other: 'o café', en: 'the coffee', gender: 'm' },
  { subject: 'A sopa', other: 'a sopa', en: 'the soup', gender: 'f' },
  { subject: 'A água', other: 'a água', en: 'the water', gender: 'f' },
  { subject: 'O dia', other: 'o dia', en: 'the day', gender: 'm' },
]

const STRENGTH_OBJECTS: Comparable[] = [
  { subject: 'O café', other: 'o café', en: 'the coffee', gender: 'm' },
  { subject: 'A conexão', other: 'a conexão', en: 'the connection', gender: 'f' },
  { subject: 'O perfume', other: 'o perfume', en: 'the perfume', gender: 'm' },
  { subject: 'O vento', other: 'o vento', en: 'the wind', gender: 'm' },
]

const INTERESTING_OBJECTS: Comparable[] = [
  { subject: 'O filme', other: 'o filme', en: 'the movie', gender: 'm' },
  { subject: 'O livro', other: 'o livro', en: 'the book', gender: 'm' },
  { subject: 'A aula', other: 'a aula', en: 'the class', gender: 'f' },
  { subject: 'A série', other: 'a série', en: 'the show', gender: 'f' },
]

const TASTY_OBJECTS: Comparable[] = [
  { subject: 'O bolo', other: 'o bolo', en: 'the cake', gender: 'm' },
  { subject: 'A pizza', other: 'a pizza', en: 'the pizza', gender: 'f' },
  { subject: 'O sushi', other: 'o sushi', en: 'the sushi', gender: 'm' },
  { subject: 'A sobremesa', other: 'a sobremesa', en: 'the dessert', gender: 'f' },
]

function pickComparables(items: Comparable[], count: number): Comparable[] {
  return shuffle(items).slice(0, count)
}

function randomInt(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1))
}

function capitalize(text: string): string {
  return text.length === 0 ? text : text.charAt(0).toUpperCase() + text.slice(1)
}

// Only an object's `en` gloss carries a leading "the" ("the car"); a
// person's never does. Dropping it keeps the compact data line terse
// ("car: R$320, bike: R$210") without looking odd for either kind.
function withoutThe(text: string): string {
  return text.replace(/^the /, '')
}

// The compared items and their values as one comma-separated line, instead
// of one line per item — "car: R$320, bike: R$210" rather than two rows.
function comparisonLine(items: Comparable[], values: string[]): string {
  return capitalize(items.map((item, index) => `${withoutThe(item.en)}: ${values[index]}`).join(', '))
}

// A regular, gender-inflecting adjective driving superiority/inferiority/
// equality/relative-superlative items — high-frequency, everyday pairs
// (PRD §6.2), each with a numeric attribute so direction can be computed
// from real data rather than told to the learner, and a `pool` saying
// whether it makes sense for a person (age, height, distance) or only for a
// thing (price, speed, difficulty — nobody "costs" money).
type RegularAttribute = {
  labelEn: string
  pool: Pool
  items: Comparable[]
  masc: string
  fem: string
  // Some attributes read more naturally with a distinct antonym for the
  // "less" direction than with "menos" + the positive word — "mais novo"
  // (younger) is what Brazilians actually say, not "menos velho". When set,
  // the "less" direction uses "mais" + this word instead of "menos" + masc/fem.
  lessMasc?: string
  lessFem?: string
  // For attributes where "menos X" IS the natural, taught form (so lessMasc
  // is left unset), a common antonym is often still accepted as an equally
  // correct alternate phrasing — "mais fácil" (easier) is just as valid an
  // answer as "menos difícil" (less difficult), the same way English accepts
  // both. Grading-only: it never becomes the canonical/taught form or changes
  // the English question, just widens what counts as correct.
  lessAltMasc?: string
  lessAltFem?: string
  posEn: string
  moreEn: string
  lessEn: string
  mostEn: string
  leastEn: string
  min: number
  max: number
  display: (value: number) => string
}

export const REGULAR_ATTRIBUTES: RegularAttribute[] = [
  {
    labelEn: 'price', pool: 'objects', items: PRICE_OBJECTS, masc: 'caro', fem: 'cara', lessAltMasc: 'barato', lessAltFem: 'barata',
    posEn: 'expensive', moreEn: 'more expensive', lessEn: 'cheaper', mostEn: 'most expensive', leastEn: 'cheapest',
    min: 20, max: 500, display: (v) => `R$${v}`,
  },
  {
    labelEn: 'speed', pool: 'objects', items: SPEED_OBJECTS, masc: 'rápido', fem: 'rápida', lessAltMasc: 'lento', lessAltFem: 'lenta',
    posEn: 'fast', moreEn: 'faster', lessEn: 'slower', mostEn: 'fastest', leastEn: 'slowest',
    min: 10, max: 180, display: (v) => `${v} km/h`,
  },
  {
    labelEn: 'difficulty', pool: 'objects', items: DIFFICULTY_OBJECTS, masc: 'difícil', fem: 'difícil', lessAltMasc: 'fácil', lessAltFem: 'fácil',
    posEn: 'difficult', moreEn: 'more difficult', lessEn: 'easier', mostEn: 'most difficult', leastEn: 'easiest',
    min: 1, max: 10, display: (v) => `${v}/10 difficulty`,
  },
  {
    labelEn: 'height', pool: 'people', items: PEOPLE, masc: 'alto', fem: 'alta', lessAltMasc: 'baixo', lessAltFem: 'baixa',
    posEn: 'tall', moreEn: 'taller', lessEn: 'shorter', mostEn: 'tallest', leastEn: 'shortest',
    min: 150, max: 200, display: (v) => `${v} cm tall`,
  },
  {
    labelEn: 'age', pool: 'people', items: PEOPLE, masc: 'velho', fem: 'velha', lessMasc: 'novo', lessFem: 'nova',
    posEn: 'old', moreEn: 'older', lessEn: 'younger', mostEn: 'oldest', leastEn: 'youngest',
    min: 18, max: 65, display: (v) => `${v} years old`,
  },
  {
    labelEn: 'distance', pool: 'people', items: PEOPLE, masc: 'longe', fem: 'longe', lessMasc: 'perto', lessFem: 'perto',
    posEn: 'far away', moreEn: 'farther away', lessEn: 'closer', mostEn: 'farthest away', leastEn: 'closest',
    min: 1, max: 40, display: (v) => `${v} km away`,
  },
  {
    labelEn: 'temperature', pool: 'objects', items: TEMPERATURE_OBJECTS, masc: 'quente', fem: 'quente', lessMasc: 'frio', lessFem: 'fria',
    posEn: 'hot', moreEn: 'hotter', lessEn: 'colder', mostEn: 'hottest', leastEn: 'coldest',
    min: 0, max: 100, display: (v) => `${v}°C`,
  },
  {
    labelEn: 'strength', pool: 'objects', items: STRENGTH_OBJECTS, masc: 'forte', fem: 'forte', lessMasc: 'fraco', lessFem: 'fraca',
    posEn: 'strong', moreEn: 'stronger', lessEn: 'weaker', mostEn: 'strongest', leastEn: 'weakest',
    min: 1, max: 10, display: (v) => `${v}/10 strength`,
  },
  {
    labelEn: 'how interesting', pool: 'objects', items: INTERESTING_OBJECTS, masc: 'interessante', fem: 'interessante', lessMasc: 'chato', lessFem: 'chata',
    posEn: 'interesting', moreEn: 'more interesting', lessEn: 'more boring', mostEn: 'most interesting', leastEn: 'most boring',
    min: 1, max: 10, display: (v) => `${v}/10 interesting`,
  },
  {
    labelEn: 'tastiness', pool: 'objects', items: TASTY_OBJECTS, masc: 'gostoso', fem: 'gostosa',
    posEn: 'tasty', moreEn: 'tastier', lessEn: 'less tasty', mostEn: 'tastiest', leastEn: 'least tasty',
    min: 1, max: 10, display: (v) => `${v}/10 tasty`,
  },
  {
    labelEn: 'tiredness', pool: 'people', items: PEOPLE, masc: 'cansado', fem: 'cansada',
    posEn: 'tired', moreEn: 'more tired', lessEn: 'less tired', mostEn: 'most tired', leastEn: 'least tired',
    min: 1, max: 10, display: (v) => `${v}/10 tired`,
  },
  {
    labelEn: 'how busy', pool: 'people', items: PEOPLE, masc: 'ocupado', fem: 'ocupada', lessMasc: 'livre', lessFem: 'livre',
    posEn: 'busy', moreEn: 'busier', lessEn: 'freer', mostEn: 'busiest', leastEn: 'freest',
    min: 1, max: 10, display: (v) => `${v}/10 busy`,
  },
  {
    labelEn: 'intelligence', pool: 'people', items: PEOPLE, masc: 'inteligente', fem: 'inteligente',
    posEn: 'smart', moreEn: 'smarter', lessEn: 'less smart', mostEn: 'smartest', leastEn: 'least smart',
    min: 1, max: 10, display: (v) => `${v}/10 smart`,
  },
  {
    labelEn: 'wealth', pool: 'people', items: PEOPLE, masc: 'rico', fem: 'rica', lessMasc: 'pobre', lessFem: 'pobre',
    posEn: 'rich', moreEn: 'richer', lessEn: 'poorer', mostEn: 'richest', leastEn: 'poorest',
    min: 1, max: 10, display: (v) => `${v}/10 rich`,
  },
]

// Resolves which word ("mais"/"menos") and which adjective form to use for a
// given direction — swapping in the natural antonym (mais novo, not menos
// velho) when the attribute defines one.
function wordAndAdjective(attr: RegularAttribute, direction: 'more' | 'less', gender: Gender): { word: string; adjective: string } {
  if (direction === 'more') return { word: 'mais', adjective: adjectiveFor(attr, gender) }
  if (attr.lessMasc && attr.lessFem) {
    return { word: 'mais', adjective: gender === 'f' ? attr.lessFem : attr.lessMasc }
  }
  return { word: 'menos', adjective: adjectiveFor(attr, gender) }
}

// The four suppletive irregulars, paired by axis (quality: bom/ruim → melhor/
// pior; size: grande/pequeno → maior/menor). Unlike the regular set, the
// comparative word itself carries the direction — there's no mais/menos
// choice — so "more"/"less" map straight to a specific word, not an
// inflection. Both describe things, not people (nobody "is 4/5 stars").
type IrregularAttribute = {
  labelEn: string
  items: Comparable[]
  more: string
  less: string
  moreSuperM: string
  moreSuperF: string
  lessSuperM: string
  lessSuperF: string
  moreEn: string
  lessEn: string
  mostEn: string
  leastEn: string
  min: number
  max: number
  display: (value: number) => string
}

export const IRREGULAR_ATTRIBUTES: IrregularAttribute[] = [
  {
    labelEn: 'quality', items: QUALITY_OBJECTS, more: 'melhor', less: 'pior', moreSuperM: 'o melhor', moreSuperF: 'a melhor', lessSuperM: 'o pior', lessSuperF: 'a pior',
    moreEn: 'better', lessEn: 'worse', mostEn: 'the best', leastEn: 'the worst',
    min: 1, max: 5, display: (v) => `${v}/5 stars`,
  },
  {
    labelEn: 'size', items: SIZE_OBJECTS, more: 'maior', less: 'menor', moreSuperM: 'o maior', moreSuperF: 'a maior', lessSuperM: 'o menor', lessSuperF: 'a menor',
    moreEn: 'bigger', lessEn: 'smaller', mostEn: 'the biggest', leastEn: 'the smallest',
    min: 10, max: 200, display: (v) => `${v} m²`,
  },
]

// Quantity nouns driving tanto/tanta/tantos/tantas agreement (always an
// equality item — PRD scope has no mais/menos quantity comparison). Always
// people — money/patience/friends are things a person has. Each carries a
// numeric range + display so the item can show equal DATA for both people
// (like every other comparison type) instead of just asserting "they both
// have a lot" — the learner sees why they're equal, not just being told to
// translate a given sentence.
export type QuantityNoun = { word: string; tantoForm: string; enWord: string; min: number; max: number; display: (value: number) => string }

export const QUANTITY_NOUNS: QuantityNoun[] = [
  { word: 'dinheiro', tantoForm: 'tanto', enWord: 'money', min: 50, max: 5000, display: (v) => `R$${v}` },
  { word: 'paciência', tantoForm: 'tanta', enWord: 'patience', min: 1, max: 10, display: (v) => `${v}/10 patience` },
  { word: 'amigos', tantoForm: 'tantos', enWord: 'friends', min: 2, max: 30, display: (v) => `${v} friends` },
  { word: 'tempo livre', tantoForm: 'tanto', enWord: 'free time', min: 1, max: 20, display: (v) => `${v} hrs/week free` },
  { word: 'livros', tantoForm: 'tantos', enWord: 'books', min: 3, max: 100, display: (v) => `${v} books` },
  { word: 'roupas', tantoForm: 'tantas', enWord: 'clothes', min: 5, max: 80, display: (v) => `${v} clothes` },
  { word: 'energia', tantoForm: 'tanta', enWord: 'energy', min: 1, max: 10, display: (v) => `${v}/10 energy` },
  { word: 'problemas', tantoForm: 'tantos', enWord: 'problems', min: 1, max: 20, display: (v) => `${v} problems` },
  { word: 'trabalho', tantoForm: 'tanto', enWord: 'work', min: 1, max: 10, display: (v) => `${v}/10 workload` },
  { word: 'sorte', tantoForm: 'tanta', enWord: 'luck', min: 1, max: 10, display: (v) => `${v}/10 luck` },
]

// Preference objects for the verb-based category — high-frequency, everyday
// things people actually say they like/prefer/want.
type PreferenceObject = { pt: string; en: string }

const PREFERENCE_OBJECTS: PreferenceObject[] = [
  { pt: 'pizza', en: 'pizza' },
  { pt: 'praia', en: 'the beach' },
  { pt: 'futebol', en: 'soccer' },
  { pt: 'café', en: 'coffee' },
  { pt: 'chocolate', en: 'chocolate' },
  { pt: 'música', en: 'music' },
  { pt: 'esse filme', en: 'that movie' },
  { pt: 'viajar', en: 'traveling' },
  { pt: 'cerveja', en: 'beer' },
  { pt: 'séries', en: 'TV shows' },
  { pt: 'dançar', en: 'dancing' },
  { pt: 'games', en: 'video games' },
]

export type PreferenceVerb = { pt: string; en: string }

export const PREFERENCE_VERBS: PreferenceVerb[] = [
  { pt: 'gosto', en: 'like' },
  { pt: 'prefiro', en: 'prefer' },
  { pt: 'quero', en: 'want' },
  { pt: 'amo', en: 'love' },
  { pt: 'adoro', en: 'adore' },
]

// A second verb-based pattern ("precisar de") with its own dedicated pool —
// things people actually say they need more/less of. Keeps buildCompareWithVerbItem's
// "gostar de" from being the only verb in the "more/less of a thing" pattern.
type NeedObject = { pt: string; en: string }

const NEED_OBJECTS: NeedObject[] = [
  { pt: 'dinheiro', en: 'money' },
  { pt: 'tempo', en: 'time' },
  { pt: 'férias', en: 'a vacation' },
  { pt: 'ajuda', en: 'help' },
  { pt: 'espaço', en: 'space' },
  { pt: 'paciência', en: 'patience' },
]

const adjectiveFor = (attr: RegularAttribute, gender: Gender) => (gender === 'f' ? attr.fem : attr.masc)
const superlativeArticle = (gender: Gender) => (gender === 'f' ? 'a' : 'o')

// Vocab hints: opt-in word lookups shown only if the learner asks, covering
// just the raw nouns/adjectives a prompt uses — never the mais/menos
// direction, the melhor/pior/maior/menor word, or the tanto agreement, since
// those are the actual grammar point each generator is testing.

// Masc/fem pairs in this dataset always share a prefix and differ only in
// their final letter(s) (velho/velha, novo/nova) — so the hint can show the
// short "velho/a" form instead of spelling out both words in full.
function abbreviateGenderPair(masc: string, fem: string): string {
  if (masc === fem) return masc
  let shared = 0
  while (shared < masc.length && shared < fem.length && masc[shared] === fem[shared]) shared += 1
  return `${masc}/${fem.slice(shared)}`
}

function baseAdjectiveHint(attr: RegularAttribute): { en: string; pt: string } {
  return { en: attr.posEn, pt: abbreviateGenderPair(attr.masc, attr.fem) }
}

// Two-way and superlative items also surface the antonym word (frio, novo,
// perto…) when the attribute has one, since that's a distinct vocabulary
// item — but never says which direction (more/less) it belongs to.
function adjectiveHints(attr: RegularAttribute): Array<{ en: string; pt: string }> {
  const hints = [baseAdjectiveHint(attr)]
  if (attr.lessMasc && attr.lessFem) {
    hints.push({ en: attr.lessEn, pt: abbreviateGenderPair(attr.lessMasc, attr.lessFem) })
  }
  if (attr.lessAltMasc && attr.lessAltFem) {
    hints.push({ en: attr.lessEn, pt: abbreviateGenderPair(attr.lessAltMasc, attr.lessAltFem) })
  }
  return hints
}

// A person's name needs no translation — only an "objects" pool's nouns are
// real vocabulary worth hinting.
function itemHints(pool: Pool, items: Comparable[]): Array<{ en: string; pt: string }> {
  return pool === 'objects' ? items.map((item) => ({ en: withoutThe(item.en), pt: item.other })) : []
}

// ---------------------------------------------------------------------------
// "Comparison" group — two things: superiority / inferiority / equality /
// irregular comparative
// ---------------------------------------------------------------------------

// Comparisons work both ways — "A is more X than B" and "B is less X than A"
// are the same fact — so both phrasings are graded correct regardless of
// which direction the question happens to be asked from. The question still
// names one direction (to give a concrete "who is X?" to answer), chosen at
// random per item so a learner sees both "more" and "least" framings.
export function buildTwoWayComparison(attr: RegularAttribute): ComparativePrompt {
  const [a, b] = pickComparables(attr.items, 2)
  const valueA = randomInt(attr.min, attr.max)
  let valueB = randomInt(attr.min, attr.max)
  while (valueB === valueA) valueB = randomInt(attr.min, attr.max)

  const higher = valueA > valueB ? a : b
  const lower = valueA > valueB ? b : a

  const moreForm = wordAndAdjective(attr, 'more', higher.gender)
  const lessForm = wordAndAdjective(attr, 'less', lower.gender)
  const moreCore = `${higher.subject} é ${moreForm.word} ${moreForm.adjective} do que ${lower.other}`
  const lessCore = `${lower.subject} é ${lessForm.word} ${lessForm.adjective} do que ${higher.other}`
  const acceptedCores = [moreCore, moreCore.replace(' do que ', ' que '), lessCore, lessCore.replace(' do que ', ' que ')]

  // "Menos difícil" and "mais fácil" are equally correct ways to say the same
  // thing — accept the antonym paraphrase too when the attribute has one,
  // without making it the taught/canonical form (see lessAltMasc/lessAltFem).
  if (attr.lessAltMasc && attr.lessAltFem) {
    const lessAltAdjective = lower.gender === 'f' ? attr.lessAltFem : attr.lessAltMasc
    const lessAltCore = `${lower.subject} é mais ${lessAltAdjective} do que ${higher.other}`
    acceptedCores.push(lessAltCore, lessAltCore.replace(' do que ', ' que '))
  }

  const askMore = Math.random() < 0.5
  const focus = askMore ? higher : lower
  const other = askMore ? lower : higher
  const focusEn = askMore ? attr.moreEn : attr.lessEn

  return {
    promptLines: [comparisonLine([a, b], [attr.display(valueA), attr.display(valueB)]), `Is ${focus.en} ${focusEn} than ${other.en}?`],
    core: askMore ? moreCore : lessCore,
    acceptedCores,
    translation: '',
    vocabHints: [...itemHints(attr.pool, [a, b]), ...adjectiveHints(attr)],
  }
}

export function buildEqualityAdjectiveItem(attr: RegularAttribute): ComparativePrompt {
  const [a, b] = pickComparables(attr.items, 2)
  const value = randomInt(attr.min, attr.max)
  const adjA = adjectiveFor(attr, a.gender)
  const adjB = adjectiveFor(attr, b.gender)
  const core = `${a.subject} é tão ${adjA} quanto ${b.other}`
  const flipped = `${b.subject} é tão ${adjB} quanto ${a.other}`

  return {
    promptLines: [comparisonLine([a, b], [attr.display(value), attr.display(value)]), `Is ${a.en} as ${attr.posEn} as ${b.en}?`],
    core,
    acceptedCores: [core, core.replace(' quanto ', ' como '), flipped, flipped.replace(' quanto ', ' como ')],
    translation: '',
    vocabHints: [...itemHints(attr.pool, [a, b]), baseAdjectiveHint(attr)],
  }
}

export function buildEqualityNounItem(noun: QuantityNoun): ComparativePrompt {
  const [a, b] = pickComparables(PEOPLE, 2)
  const value = randomInt(noun.min, noun.max)
  const core = `${a.subject} tem ${noun.tantoForm} ${noun.word} quanto ${b.other}`
  const flipped = `${b.subject} tem ${noun.tantoForm} ${noun.word} quanto ${a.other}`
  // Plural tantoForm (tantos/tantas) means a countable English noun ("as many
  // friends"); singular (tanto/tanta) means a mass noun ("as much energy").
  const quantifier = noun.tantoForm === 'tantos' || noun.tantoForm === 'tantas' ? 'many' : 'much'

  return {
    // Same shape as the adjective-equality item ("Is X as tall as Y?") — show
    // equal DATA for both people, then a question phrased the same way the
    // answer is built (subject "has as much/many ... as" the other), so the
    // learner isn't translating a differently-shaped English sentence.
    promptLines: [comparisonLine([a, b], [noun.display(value), noun.display(value)]), `Does ${a.en} have as ${quantifier} ${noun.enWord} as ${b.en}?`],
    core,
    acceptedCores: [core, core.replace(' quanto ', ' como '), flipped, flipped.replace(' quanto ', ' como ')],
    translation: '',
    vocabHints: [{ en: noun.enWord, pt: noun.word }],
  }
}

export function buildIrregularComparisonItem(attr: IrregularAttribute): ComparativePrompt {
  const [a, b] = pickComparables(attr.items, 2)
  const valueA = randomInt(attr.min, attr.max)
  let valueB = randomInt(attr.min, attr.max)
  while (valueB === valueA) valueB = randomInt(attr.min, attr.max)

  const higher = valueA > valueB ? a : b
  const lower = valueA > valueB ? b : a

  // Same "works both ways" logic as buildTwoWayComparison: both phrasings
  // are correct, so both are accepted regardless of which one the question
  // happens to ask about.
  const moreCore = `${higher.subject} é ${attr.more} do que ${lower.other}`
  const lessCore = `${lower.subject} é ${attr.less} do que ${higher.other}`
  const acceptedCores = [moreCore, moreCore.replace(' do que ', ' que '), lessCore, lessCore.replace(' do que ', ' que ')]

  const askMore = Math.random() < 0.5
  const focus = askMore ? higher : lower
  const other = askMore ? lower : higher
  const focusEn = askMore ? attr.moreEn : attr.lessEn

  return {
    promptLines: [comparisonLine([a, b], [attr.display(valueA), attr.display(valueB)]), `Is ${focus.en} ${focusEn} than ${other.en}?`],
    core: askMore ? moreCore : lessCore,
    acceptedCores,
    translation: '',
    // The comparative word itself (melhor/pior/maior/menor) is the whole
    // point of this category, so it's never in the hint — only the nouns.
    vocabHints: itemHints('objects', [a, b]),
  }
}

// ---------------------------------------------------------------------------
// "Superlative" group — one item picked out of a group of 3+
// ---------------------------------------------------------------------------

// A superlative can end three grammatically valid ways in Portuguese — "de
// todos" (of all), "dos três" (of the three, since a superlative item always
// compares exactly three), or nothing at all when the group is obvious from
// context. Mixed randomly per item, with the English question naming the
// matching tail, so the learner knows which one this item expects instead of
// having to guess a fourth hidden thing on top of the mais/menos direction.
type SuperlativeEnding = 'all' | 'three' | 'bare'

const SUPERLATIVE_ENDINGS: SuperlativeEnding[] = ['all', 'three', 'bare']

function superlativeSuffix(ending: SuperlativeEnding): string {
  if (ending === 'all') return ' de todos'
  if (ending === 'three') return ' dos três'
  return ''
}

function superlativeQuestionTail(ending: SuperlativeEnding): string {
  if (ending === 'all') return ' of all'
  if (ending === 'three') return ' of the three'
  return ''
}

export function buildRelativeSuperlativeRegular(attr: RegularAttribute): ComparativePrompt {
  const subjects = pickComparables(attr.items, 3)
  let values: number[]
  do {
    values = subjects.map(() => randomInt(attr.min, attr.max))
  } while (new Set(values).size < 3)

  const direction: 'highest' | 'lowest' = Math.random() < 0.5 ? 'highest' : 'lowest'
  const winnerIndex = direction === 'highest' ? values.indexOf(Math.max(...values)) : values.indexOf(Math.min(...values))
  const winner = subjects[winnerIndex]
  const article = superlativeArticle(winner.gender)
  const { word, adjective: adj } = wordAndAdjective(attr, direction === 'highest' ? 'more' : 'less', winner.gender)
  const ending = SUPERLATIVE_ENDINGS[randomInt(0, SUPERLATIVE_ENDINGS.length - 1)]
  const core = `${winner.subject} é ${article} ${word} ${adj}${superlativeSuffix(ending)}`

  // Portuguese always builds the "highest" direction as "mais" + the adjective,
  // so the English mostEn gloss ("tallest", "most expensive") always lines up
  // structurally. But the "lowest" direction only has a distinct English word
  // (leastEn: "closest", "youngest") when the attribute has a true Portuguese
  // antonym (lessMasc/lessFem, e.g. perto, novo) — otherwise Portuguese just
  // says "menos" + the same adjective (menos difícil), so the English
  // question should say "least X" too, not reach for an unrelated synonym
  // like "easiest" that would point the learner at the wrong Portuguese word.
  const lowEn = attr.lessMasc ? attr.leastEn : `least ${attr.posEn}`
  const superlativeEn = direction === 'highest' ? attr.mostEn : lowEn
  const questionLine = `${attr.pool === 'people' ? 'Who' : 'Which'} is the ${superlativeEn}${superlativeQuestionTail(ending)}?`

  // Same antonym leniency as buildTwoWayComparison — "a mais fácil" is just
  // as correct as "a menos difícil" for the "lowest" direction.
  const acceptedCores = [core]
  if (direction === 'lowest' && attr.lessAltMasc && attr.lessAltFem) {
    const altAdjective = winner.gender === 'f' ? attr.lessAltFem : attr.lessAltMasc
    acceptedCores.push(`${winner.subject} é ${article} mais ${altAdjective}${superlativeSuffix(ending)}`)
  }

  return {
    promptLines: [comparisonLine(subjects, values.map((value) => attr.display(value))), questionLine],
    core,
    acceptedCores,
    translation: '',
    vocabHints: [...itemHints(attr.pool, subjects), ...adjectiveHints(attr)],
  }
}

export function buildRelativeSuperlativeIrregular(attr: IrregularAttribute): ComparativePrompt {
  const subjects = pickComparables(attr.items, 3)
  let values: number[]
  do {
    values = subjects.map(() => randomInt(attr.min, attr.max))
  } while (new Set(values).size < 3)

  const direction: 'highest' | 'lowest' = Math.random() < 0.5 ? 'highest' : 'lowest'
  const winnerIndex = direction === 'highest' ? values.indexOf(Math.max(...values)) : values.indexOf(Math.min(...values))
  const winner = subjects[winnerIndex]
  const superWord =
    direction === 'highest'
      ? winner.gender === 'f' ? attr.moreSuperF : attr.moreSuperM
      : winner.gender === 'f' ? attr.lessSuperF : attr.lessSuperM
  const ending = SUPERLATIVE_ENDINGS[randomInt(0, SUPERLATIVE_ENDINGS.length - 1)]
  const core = `${winner.subject} é ${superWord}${superlativeSuffix(ending)}`

  return {
    // Irregular attributes only ever compare things, never people, so the
    // question is always "Which" — and mostEn/leastEn already carry "the"
    // (e.g. "the best"), unlike the regular attributes' bare adjective forms.
    promptLines: [
      comparisonLine(subjects, values.map((value) => attr.display(value))),
      `Which is ${direction === 'highest' ? attr.mostEn : attr.leastEn}${superlativeQuestionTail(ending)}?`,
    ],
    core,
    acceptedCores: [core],
    translation: '',
    vocabHints: itemHints('objects', subjects),
  }
}

// ---------------------------------------------------------------------------
// "Comparison with a verb" group — liking/preferring/wanting the most/least
// ---------------------------------------------------------------------------

export function buildCompareWithVerbItem(direction: 'more' | 'less'): ComparativePrompt {
  const options = shuffle(PREFERENCE_OBJECTS).slice(0, 3)
  const winner = options[randomInt(0, options.length - 1)]
  const word = direction === 'more' ? 'mais' : 'menos'
  const core = `Eu gosto ${word} de ${winner.pt}`

  return {
    // Names the winner directly rather than asking the learner to guess which
    // of the three options is meant — the options list gives context (and
    // vocab), but the instruction itself is unambiguous about what to say.
    promptLines: [
      `Options: ${options.map((option) => option.en).join(', ')}`,
      `Say you like ${winner.en} the ${direction === 'more' ? 'most' : 'least'}.`,
    ],
    core,
    acceptedCores: [core, `${core} do que de tudo`],
    translation: '',
    vocabHints: [{ en: 'like', pt: 'gostar' }, ...options.map((option) => ({ en: option.en, pt: option.pt }))],
  }
}

// A second "more/less of a thing" pattern using "precisar de" (need) instead
// of "gostar de" (like) — same shape as buildCompareWithVerbItem, but its own
// verb and its own pool of things people actually say they need more/less of
// (money, time, a vacation…), so the "verb" category isn't just gostar twice.
export function buildNeedMoreLessItem(direction: 'more' | 'less'): ComparativePrompt {
  const options = shuffle(NEED_OBJECTS).slice(0, 3)
  const winner = options[randomInt(0, options.length - 1)]
  const word = direction === 'more' ? 'mais' : 'menos'
  const core = `Eu preciso ${word} de ${winner.pt}`

  return {
    // Same reasoning as buildCompareWithVerbItem: name the winner directly
    // instead of leaving the learner to guess which option is meant.
    promptLines: [
      `Options: ${options.map((option) => option.en).join(', ')}`,
      `Say you need ${winner.en} the ${direction === 'more' ? 'most' : 'least'}.`,
    ],
    core,
    acceptedCores: [core, `${core} do que de tudo`],
    translation: '',
    vocabHints: [{ en: 'need', pt: 'precisar' }, ...options.map((option) => ({ en: option.en, pt: option.pt }))],
  }
}

export function buildMostLeastVerbItem(verb: PreferenceVerb): ComparativePrompt {
  const object = PREFERENCE_OBJECTS[randomInt(0, PREFERENCE_OBJECTS.length - 1)]
  const direction: 'more' | 'less' = Math.random() < 0.5 ? 'more' : 'less'
  const word = direction === 'more' ? 'mais' : 'menos'
  const core = `Isto é o que eu ${word} ${verb.pt}`
  // "Isso" is an equally natural demonstrative here, and naming the actual
  // thing being talked about instead of a demonstrative at all ("Chocolate é
  // o que eu mais gosto") is just as correct — accept both alongside "isto".
  const isso = `Isso é o que eu ${word} ${verb.pt}`
  const namedSubject = `${capitalize(object.pt)} é o que eu ${word} ${verb.pt}`

  return {
    promptLines: [
      `You are talking about ${object.en}.`,
      `Say it's the thing you ${verb.en} the ${direction === 'more' ? 'most' : 'least'}.`,
    ],
    core,
    acceptedCores: [core, isso, namedSubject],
    translation: '',
    // The noun is worth hinting now too — it's not just flavor text, it's
    // what "namedSubject" above actually needs the learner to know.
    vocabHints: [{ en: verb.en, pt: verb.pt }, { en: object.en, pt: object.pt }],
  }
}

// ---------------------------------------------------------------------------
// Session building
// ---------------------------------------------------------------------------

type GeneratorEntry = { form: ComparativeForm; build: () => ComparativePrompt }

const GENERATORS: Record<ComparativeCategory, GeneratorEntry[]> = {
  // One entry per attribute (rather than one entry covering all attributes)
  // so the "possible prompts" count reflects real content variety — each
  // attribute is a meaningfully different thing to practice, not just a
  // different roll of the same generator.
  adjective: [
    ...REGULAR_ATTRIBUTES.map((attr): GeneratorEntry => ({ form: 'regular', build: () => buildTwoWayComparison(attr) })),
    ...REGULAR_ATTRIBUTES.map((attr): GeneratorEntry => ({ form: 'regular', build: () => buildEqualityAdjectiveItem(attr) })),
    ...IRREGULAR_ATTRIBUTES.map((attr): GeneratorEntry => ({ form: 'irregular', build: () => buildIrregularComparisonItem(attr) })),
  ],
  superlative: [
    ...REGULAR_ATTRIBUTES.map((attr): GeneratorEntry => ({ form: 'regular', build: () => buildRelativeSuperlativeRegular(attr) })),
    ...IRREGULAR_ATTRIBUTES.map((attr): GeneratorEntry => ({ form: 'irregular', build: () => buildRelativeSuperlativeIrregular(attr) })),
  ],
  verb: [
    ...QUANTITY_NOUNS.map((noun): GeneratorEntry => ({ form: 'regular', build: () => buildEqualityNounItem(noun) })),
    { form: 'regular', build: () => buildCompareWithVerbItem('more') },
    { form: 'regular', build: () => buildCompareWithVerbItem('less') },
    { form: 'regular', build: () => buildNeedMoreLessItem('more') },
    { form: 'regular', build: () => buildNeedMoreLessItem('less') },
    ...PREFERENCE_VERBS.map((verb): GeneratorEntry => ({ form: 'regular', build: () => buildMostLeastVerbItem(verb) })),
  ],
}

function activeGenerators(categoryIds: ComparativeCategory[], formIds: ComparativeForm[]): GeneratorEntry[] {
  const categories = categoryIds.length > 0 ? categoryIds : DEFAULT_COMPARATIVE_CATEGORY_IDS
  const forms = formIds.length > 0 ? formIds : DEFAULT_COMPARATIVE_FORM_IDS
  return categories.flatMap((id) => GENERATORS[id]).filter((entry) => forms.includes(entry.form))
}

// The number of distinct sentence patterns the current Type + Form selection
// enables — shown in the setup screen's badge. Generation is procedural
// (an unbounded number of subjects/values/attributes per pattern), so this
// counts grammatical patterns available, not a literal finite prompt pool.
export function comparativePatternCount(categoryIds: ComparativeCategory[], formIds: ComparativeForm[]): number {
  return activeGenerators(categoryIds, formIds).length
}

export function buildComparativeSession(categoryIds: ComparativeCategory[], formIds: ComparativeForm[]): ComparativePrompt[] {
  const generators = activeGenerators(categoryIds, formIds)
  if (generators.length === 0) return []
  return Array.from({ length: COMPARATIVE_SESSION_LENGTH }, () => generators[randomInt(0, generators.length - 1)].build())
}
